import isEqual from 'lodash/isEqual.js'
import {
  DragAndDropList,
  Draggable,
  DragHandle,
  MOVE_AFTER,
  MOVE_BEFORE,
} from '../../common/components/DragAndDrop.js'
import ConfirmModal from '../../common/components/Modal/ConfirmModal.js'
import {saveUserSettings} from '../../data/me.js'
import {
  formsSelector,
  getState,
  onlyIfForm,
  removeForm,
  setForm,
  setFormValue,
  updateForm,
} from '../../store.js'
import {
  buildTabsSelector,
  CAN_HIDE_TAB,
  getMainTabs,
  MAX_MAIN_TABS,
  saveableTabStateSelector,
  tabsSelector,
} from './headerSelectors.js'
import {showMessageToast} from './Toast/index.js'
import ButtonLink from '../../common/components/Button/ButtonLink.js'
import Icon from '../../common/icons/Icon.js'
import ButtonPrimary from '../../common/components/Button/ButtonPrimary.js'
import className from '../../common/className.js'

export const MODAL_FORM = 'edit_top_nav_modal'

export function showEditTopNavModal() {
  const placeholdersByID = Array(MAX_MAIN_TABS)
    .fill(null)
    .reduce((prev, value, index) => {
      const id = `__placeholder__${index}`

      prev[id] = {
        _id: id,
        _ref: {current: null},
        display: id,
        empty: true,
        overflow: false,
      }

      return prev
    }, {})

  const tabs = tabsSelector(getState()).map((tab) => ({
    ...tab,
    _id: tab.display,
    _ref: {current: null},
  }))

  setForm(MODAL_FORM, {
    ...organizeTabs(tabs, placeholdersByID),
    isSaving: false,
    serverError: null,
  })
}

function organizeTabs(
  tabs,
  placeholdersByID = modalFormSelector(getState())?.placeholdersByID,
) {
  const mainTabs = getMainTabs(tabs)
  const overflowTabs = tabs.filter((tab) => !mainTabs.includes(tab))

  tabs = [
    ...mainTabs,
    ...Array(MAX_MAIN_TABS - mainTabs.length)
      .fill(null)
      .map(
        (tab, index) =>
          placeholdersByID[`__placeholder__${MAX_MAIN_TABS - (index + 1)}`],
      )
      .reverse(),
    ...overflowTabs,
  ]

  const [list, tabsByID] = tabs.reduce(
    (prev, tab) => {
      prev[0].push(tab.display)
      prev[1][tab.display] = tab
      return prev
    },
    [[], {}],
  )

  return {tabsByID, list, overflowIndex: MAX_MAIN_TABS, placeholdersByID}
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

function resetToDefault() {
  const tabs = buildTabsSelector(getState())

  updateModalForm(organizeTabs(tabs))
}

async function saveTabs() {
  try {
    const {list, tabsByID} = modalFormSelector(getState())

    const tabs = saveableTabStateSelector(getState(), {
      tabs: list.reduce((prev, id) => {
        const tab = tabsByID[id]

        // remove placeholders
        if (!tab.empty) {
          prev.push(tab)
        }

        return prev
      }, []),
    })

    updateModalForm({serverError: null, isSaving: true})

    await saveUserSettings({tabs})

    showMessageToast('Saved Navigation Tabs')

    closeModal()
  } catch (err) {
    updateModalForm({
      serverError: err.message || err.error_message,
      isSaving: false,
    })
  }
}

function onFilterMove([value], nearValue, where) {
  const {list: originalList, tabsByID} = modalFormSelector(getState())

  // was moved item in main list
  const wasMain = tabsByID[value]?.overflow === false
  // is near item in main list
  const nearIsMain = tabsByID[nearValue]?.overflow === false

  // get sorted items and exclude moving item
  const list = originalList.filter((item) => item !== value)

  // get index of near item
  const nearIndex = list.indexOf(nearValue)

  // flip `where` if moving between lists
  if (wasMain !== nearIsMain) {
    where = where === MOVE_BEFORE ? MOVE_AFTER : MOVE_BEFORE
  }

  // move moved tab to/from overflow
  tabsByID[value].overflow = !nearIsMain

  // put moving item in it's place
  if (where === MOVE_BEFORE) {
    list.splice(nearIndex, 0, value)
  } else if (where === MOVE_AFTER) {
    list.splice(nearIndex + 1, 0, value)
  }

  if (!isEqual(list, originalList)) {
    rebuildList(list)
  }
}

function onFinished() {
  rebuildList()
}

function rebuildList(list) {
  const form = modalFormSelector(getState())
  const {tabsByID} = form
  list ||= form.list

  const mainList = []
  const overflowList = []
  let newTabs = list.map((id) => tabsByID[id])

  // filter out empty placeholders
  newTabs = newTabs.filter(({empty}) => !empty)

  // loop through new list order
  for (const [index, tab] of newTabs.entries()) {
    const id = tab.display

    // we added too many to main list so move excess to overflow
    if (index >= MAX_MAIN_TABS && tab.overflow === false) {
      // if placeholder has moved to overflow throw it away
      if (tab.empty) {
        continue
      }

      tab.overflow = true
    }

    // rebuild each list
    if (tab.overflow === false) {
      mainList.push(id)
    } else {
      overflowList.push(id)
    }
  }

  // Keep at least one tab in the main list
  if (mainList.length === 0) {
    const singleID = overflowList.shift()
    tabsByID[singleID].overflow = false
    mainList.push(singleID)
  }

  updateModalForm(
    organizeTabs([
      ...mainList.map((id) => tabsByID[id]),
      ...overflowList.map((id) => tabsByID[id]),
    ]),
  )
}

function Tab({tab, index}) {
  const isPlaceholder = !!tab.empty

  return (
    <Draggable
      className={className`flex wrap--draggable-item ${{'op-50': tab.hide}}`}
      id={tab.display}
      uiIndex={index}
      onDrop={onFinished}
      ref={tab._ref}
    >
      {!isPlaceholder && (
        <DragHandle className="padding-top-10 padding-bottom-10 border-top--light" />
      )}
      <div
        className={className`fs-01 flex--justify align-items-center border-top--light padding-top-10 padding-bottom-10 flex-grow ${{'drag-item--placeholder': isPlaceholder}}`}
      >
        {isPlaceholder ? (
          <span className="fs-n0 lh-md">EMPTY NAV SLOT {index + 1}</span>
        ) : (
          tab.display
        )}
        {CAN_HIDE_TAB.includes(tab.type) && (
          <ButtonLink
            onClick={() =>
              setFormValue(
                MODAL_FORM,
                ['tabsByID', tab.display, 'hide'],
                !tab.hide,
              )
            }
            title={tab.hide ? 'Show this tab' : 'Hide this tab'}
          >
            <Icon className="fs-02 lh-0" name={tab.hide ? 'hide' : 'show'} />
          </ButtonLink>
        )}
      </div>
    </Draggable>
  )
}

function EditTopNavModal({form}) {
  return (
    <ConfirmModal
      modalSize="md"
      title="Configure Navigation"
      isSaving={form.isSaving}
      confirmText="Save"
      cancelText="Cancel"
      onConfirm={() => saveTabs()}
      onCancel={() => closeModal()}
      error={form.serverError}
      RightButtons={() => (
        <ButtonPrimary isOutlined size="sm" onClick={() => resetToDefault()}>
          Reset to Default
        </ButtonPrimary>
      )}
    >
      <p className="fs-01 lh-md margin-bottom-30 w-80">
        Drag and drop to reorder the main nav tabs or to place them in the
        overflow dropdown menu.
      </p>
      <div className="flex flex--justify-nowrap">
        <DragAndDropList onMove={onFilterMove}>
          <div className="outer-wrap--main-nav-draggable-items">
            <div className="wrap--modal-draggable-items wrap--nav-draggable-items wrap--main-nav-draggable-items">
              <p className="fs-01 lh-x-md margin-bottom-15">
                <strong>Main Horizontal Nav Tabs</strong>
              </p>
              {form.list.slice(0, form.overflowIndex).map((id, index) => (
                <Tab
                  key={form.tabsByID[id]._id}
                  tab={form.tabsByID[id]}
                  index={index}
                />
              ))}
            </div>
          </div>
          <div className="outer-wrap--main-nav-draggable-items">
            <div className="wrap--modal-draggable-items wrap--nav-draggable-items wrap--overflow-nav-draggable-items">
              <p className="fs-01 lh-md margin-bottom-15 flex align-items-center">
                <strong className="margin-right-5">
                  Overflow Dropdown Nav
                </strong>
                <span className="fs-00 lh-md op-50 flex align-items-center">
                  (
                  <Icon name="meatballs" className="fs-02 lh-0" />)
                </span>
              </p>
              {form.list.slice(form.overflowIndex).map((id, index) => (
                <Tab
                  key={form.tabsByID[id]._id}
                  tab={form.tabsByID[id]}
                  index={index + form.overflowIndex}
                />
              ))}
            </div>
          </div>
        </DragAndDropList>
      </div>
    </ConfirmModal>
  )
}

export default onlyIfForm(EditTopNavModal, modalFormSelector)
